/**
 * SegmentPreview - Preview dos contatos que match o segmento
 * 
 * Mostra contagem e lista parcial dos contatos encontrados.
 * 
 * @author Sistema de Campanhas
 * @version 1.0.0
 */

import { RefreshCw, Users, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { SegmentPreviewContact } from '@/types/campaign-segments';
import { cn } from '@/lib/utils';

interface SegmentPreviewProps {
  contacts: SegmentPreviewContact[];
  totalCount: number;
  isTruncated: boolean;
  isLoading: boolean;
  isError: boolean;
  hasValidFilters: boolean;
  onRefresh: () => void;
}

/**
 * Componente de preview do segmento
 */
export function SegmentPreview({
  contacts,
  totalCount,
  isTruncated,
  isLoading,
  isError,
  hasValidFilters,
  onRefresh,
}: SegmentPreviewProps) {
  // Estado: Sem filtros válidos
  if (!hasValidFilters) {
    return (
      <Card>
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Users className="h-4 w-4" />
            Preview do Segmento
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-4">
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <AlertCircle className="h-8 w-8 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">
              Adicione pelo menos um filtro com valor para ver o preview
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Estado: Carregando
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Users className="h-4 w-4" />
            Preview do Segmento
            <RefreshCw className="h-3 w-3 animate-spin ml-auto" />
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-4 space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-center gap-3 p-2">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  // Estado: Erro
  if (isError) {
    return (
      <Card className="border-destructive">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm font-medium flex items-center gap-2 text-destructive">
            <AlertCircle className="h-4 w-4" />
            Erro ao carregar preview
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Não foi possível buscar os contatos. Tente novamente.
            </p>
            <Button variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw className="h-3 w-3 mr-1" />
              Tentar novamente
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Estado: Sem resultados
  if (contacts.length === 0) {
    return (
      <Card>
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Users className="h-4 w-4" />
            Preview do Segmento
            <Badge variant="outline" className="ml-auto">
              0 contatos
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-4">
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <Users className="h-8 w-8 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">
              Nenhum contato encontrado com os filtros selecionados
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Tente ajustar os filtros para ampliar a busca
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Estado: Com resultados
  return (
    <Card className="border-primary/30">
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-primary" />
            Preview do Segmento
            <Badge variant="default">
              {totalCount.toLocaleString('pt-BR')} contato{totalCount !== 1 ? 's' : ''}
              {isTruncated && '+'}
            </Badge>
          </CardTitle>
          
          <Button variant="ghost" size="sm" onClick={onRefresh}>
            <RefreshCw className="h-3 w-3 mr-1" />
            Atualizar
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="pb-4">
        <ScrollArea className="h-[200px]">
          <div className="space-y-1">
            {contacts.slice(0, 10).map((contact, index) => (
              <div
                key={contact.id}
                className={cn(
                  "flex items-center gap-3 p-2 rounded-lg",
                  index % 2 === 0 ? "bg-muted/30" : ""
                )}
              >
                {/* Avatar placeholder */}
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary">
                  {(contact.name || '?')[0].toUpperCase()}
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {contact.name || 'Sem nome'}
                  </p>
                  <p className="text-xs text-muted-foreground font-mono">
                    {contact.phone}
                  </p>
                </div>
                
                {contact.match_reason && (
                  <Badge variant="outline" className="text-xs shrink-0">
                    {contact.match_reason}
                  </Badge>
                )}
              </div>
            ))}
            
            {/* Indicador de mais contatos */}
            {contacts.length > 10 && (
              <div className="text-center py-2 text-sm text-muted-foreground">
                ... e mais {contacts.length - 10} contato{contacts.length - 10 !== 1 ? 's' : ''}
              </div>
            )}
            
            {isTruncated && (
              <div className="text-center py-2 text-xs text-muted-foreground bg-muted/50 rounded">
                Limite de preview atingido. O segmento pode conter mais contatos.
              </div>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
